import type { OMSView } from '@src/components/OMS/OMSView';
import type { RfqBestQuoteBy } from '@src/utils/rfq';
import {
  DateTimeDurationPickerValueType,
  DEFAULT_SLIPPAGES,
  type PriceInputMode,
  type RFQTradingProtocolEnum,
  type Slippage,
} from '@talos/kyoko';
import type { OMSPopoutGroup, OMSPopoutGroupType } from '../components/OMS/types';
import type { OrderStrategiesEnum } from '../tokens/orderStrategy';

export const NewMultilegDefaultDelta1Behaviour = {
  None: 'NONE',
  PerpVPerp: 'PERP_V_PERP',
  PerpVSpot: 'PERP_V_SPOT',
  PerpVFut: 'PERP_V_FUT',
} as const;
export type NewMultilegDefaultDelta1Behaviour =
  (typeof NewMultilegDefaultDelta1Behaviour)[keyof typeof NewMultilegDefaultDelta1Behaviour];
export const NEW_MULTILEG_DEFAULT_DELTA1_BEHAVIOUR_OPTIONS = Object.values(
  NewMultilegDefaultDelta1Behaviour
) as NewMultilegDefaultDelta1Behaviour[];

export interface TradingSettingsContextProps
  extends DefaultTradingSettingsConfigType, DefaultTradingSettingsMethodsType {}

type DefaultTradingSettingsConfigType = typeof DefaultTradingSettingsConfig;
type DefaultTradingSettingsMethodsType = typeof DefaultTradingSettingsMethods;

export const DefaultTradingSettingsConfig = {
  allowSyntheticCcy: true,
  allowedSlippage: DEFAULT_SLIPPAGES[2],
  defaultSubAccountId: undefined as number | undefined,
  enableGroups: false,
  confirmOrderCancels: true,
  confirmOrderResume: true,
  allowSyntheticCrosses: false,
  rememberSubAccountId: true,
  useTradeAllocations: false,
  clickToTradeAll: false,
  clickToTradeDefaultSubaccount: undefined,
  enableCustomerPricing: false,
  customerPricingInitialExpanded: true,
  applyCustomerPricingDetailsToOrderComment: false,
  enableSalesWorkflow: false,
  linkCustomerHedgeSubaccount: false,
  enableDerivativeContractDefault: false,
  exclusivelyPrimePriceOnRePriming: false,
  enableCustomerBlotterColumns: false,
  selectedCustomerID: undefined as number | undefined,
  alwaysCheckPriceReasonability: true,
  enableCustomerTradeBookingOnCPC: false,
  defaultOrderStrategy: null as OrderStrategiesEnum | null,
  defaultMultilegOrderStrategy: null as OrderStrategiesEnum | null,
  defaultOrderFormTab: null as unknown as OMSView,
  defaultPriceOffsets: '10|25',
  alwaysUseBBOForPriceOffsets: false,
  startTimeEntryMode: DateTimeDurationPickerValueType.DateTime,
  endTimeEntryMode: DateTimeDurationPickerValueType.DateTime,
  enableAdvancedOrderDetails: undefined as boolean | undefined, // when undefined default value will depend on user roles, check AppConfigProvider
  alwaysShowBestBidOffer: false,
  alwaysShowMarketCardBestBidOffer: false,
  showOrderFormRollupSelector: false,
  useNewMarketSelector: true,
  bulkClosePositionQuoteCurrencies: ['USDT', 'USDC', 'USD'],
  enableReduceFirst: false,
  enableReduceOnly: false,
  priceOffsetBPSBehaviour: 'Increment' as PriceInputMode,
  defaultToEmptyOrderMarkets: false,
  resetOrderAccountsOnSubAccountChange: false,
  omsPopoutGroups: {} as Partial<Record<OMSPopoutGroupType, Omit<OMSPopoutGroup, '_window'>>>,
  exportDatesRaw: false,
  // This is also used for Order Book panel settings
  enableDefaultMarketCardSubAccount: false,
  defaultNewMarketCardsCompact: false,
  newMultilegDefaultDelta1Behaviour: NewMultilegDefaultDelta1Behaviour.None as NewMultilegDefaultDelta1Behaviour,
  /** User settings - Whether to keep the form open after submitting a deal */
  keepFormOpenAfterSubmit: true,
  showAllDecimalsInBlotters: false,
  rfqPricingPanelShowAllDecimals: true,
  takerQuoteBreakdownShowAllDecimals: false,
  careOrderNettingPreviewShowAllDecimals: true,
  rfqFormTradingProtocol: undefined as RFQTradingProtocolEnum | undefined,
  rfqBestQuoteBy: 'px' as RfqBestQuoteBy,
};

export const DefaultTradingSettingsMethods = {
  setAllowSyntheticCcy: (allowSyntheticCcy: boolean) => ({}),
  setAllowedSlippage: (allowedSlippage: Slippage) => ({}),
  setDefaultSubAccountId: (defaultSubAccountId: number | undefined) => ({}),
  setEnableGroups: (enableGroups: boolean) => ({}),
  setRememberSubAccountId: (rememberSubAccountId: boolean) => ({}),
  setUseTradeAllocations: (useTradeAllocations: boolean) => ({}),
  setClickToTradeAll: (clickToTradeAll: boolean) => ({}),
  setClickToTradeDefaultSubaccount: (defaultSubaccount: string) => ({}),
  setAllowSyntheticCrosses: (allowSyntheticCrosses: boolean) => ({}),
  setConfirmOrderCancels: (confirmOrderCancels: boolean) => ({}),
  setConfirmOrderResume: (confirmOrderResume: boolean) => ({}),
  setEnableCustomerPricing: (enableCustomerPricing: boolean) => ({}),
  setEnableSalesWorkflow: (enableSalesWorkflow: boolean) => ({}),
  setLinkCustomerHedgeSubaccount: (linkCustomerHedgeSubaccount: boolean) => ({}),
  setEnableDerivativeContractDefault: (enableDerivativeContractDefault: boolean) => ({}),
  setExclusivelyPrimePriceOnRePriming: (exclusivelyPrimePriceOnRePriming: boolean) => ({}),
  setEnableCustomerBlotterColumns: (enableCustomerBlotterColumns: boolean) => ({}),
  setCustomerPricingInitialExpanded: (customerPricingInitialExpanded: boolean) => ({}),
  setApplyCustomerPricingDetailsToOrderComment: (applyCustomerPricingDetailsToOrderComment: boolean) => ({}),
  setSelectedCustomerID: (selectedCustomerId: number | undefined) => ({}),
  setAlwaysCheckPriceReasonability: (alwaysCheckPriceReasonability: boolean) => ({}),
  setEnableCustomerTradeBookingOnCPC: (enableCustomerTradeBookingOnCPC: boolean) => ({}),
  setDefaultOrderStrategy: (orderStrategy: string) => ({}),
  setDefaultMultilegOrderStrategy: (orderStrategy: string) => ({}),
  setDefaultOrderFormTab: (tab: OMSView | '') => ({}),
  setDefaultPriceOffsets: (priceOffsets: string) => ({}),
  setAlwaysUseBBOForPriceOffsets: (alwaysUseBBOForPriceOffsets: boolean) => ({}),
  setStartTimeEntryMode: (startTimeEntryMode: DateTimeDurationPickerValueType) => ({}),
  setEndTimeEntryMode: (endTimeEntryMode: DateTimeDurationPickerValueType) => ({}),
  setEnableAdvancedOrderDetails: (enableAdvancedOrderDetails: boolean) => ({}),
  setAlwaysShowBestBidOffer: (alwaysShowBestBidOffer: boolean) => ({}),
  setAlwaysShowMarketCardBestBidOffer: (alwaysShowMarketCardBestBidOffer: boolean) => ({}),
  setShowOrderFormRollupSelector: (showOrderFormRollupSelector: boolean) => ({}),
  setUseNewMarketSelector: (useNewMarketSelector: boolean) => ({}),
  setBulkClosePositionQuoteCurrencies: (bulkClosePositionQuoteCurrencies: string[]) => ({}),
  setEnableReduceFirst: (enableReduceFirst: boolean) => ({}),
  setEnableReduceOnly: (enableReduceOnly: boolean) => ({}),
  setPriceOffsetBPSBehaviour: (priceOffsetBPSBehaviour: PriceInputMode) => ({}),
  setDefaultToEmptyOrderMarkets: (defaultToEmptyOrderMarkets: boolean) => ({}),
  setResetOrderAccountsOnSubAccountChange: (resetOrderAccountsOnSubAccountChange: boolean) => ({}),
  setOmsPopoutGroups: (omsPopoutGroups: Partial<Record<OMSPopoutGroupType, Omit<OMSPopoutGroup, '_window'>>>) => ({}),
  setExportDatesRaw: (shouldExportRaw: boolean) => {},
  setEnableDefaultMarketCardSubAccount: (enableDefaultMarketCardSubAccount: boolean) => {},
  setDefaultNewMarketCardsCompact: (defaultNewMarketCardsCompact: boolean) => {},
  setNewMultilegDefaultDelta1Behaviour: (newMultilegDefaultDelta1Behaviour: NewMultilegDefaultDelta1Behaviour) => {},
  setKeepFormOpenAfterSubmit: (keepFormOpenAfterSubmit: boolean) => ({}),
  setShowAllDecimalsInBlotters: (showAllDecimalsInBlotters: boolean) => ({}),
  setRfqPricingPanelShowAllDecimals: (rfqPricingPanelShowAllDecimals: boolean) => ({}),
  setTakerQuoteBreakdownShowAllDecimals: (takerQuoteBreakdownShowAllDecimals: boolean) => ({}),
  setCareOrderNettingPreviewShowAllDecimals: (careOrderNettingPreviewShowAllDecimals: boolean) => ({}),
  setRfqFormTradingProtocol: (rfqFormTradingProtocol: RFQTradingProtocolEnum | undefined) => ({}),
  setRfqBestQuoteBy: (rfqBestQuoteBy: RfqBestQuoteBy) => ({}),
};
